import React, { useContext, useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button, Form, Modal, Table, Tab, Tabs, OverlayTrigger } from "react-bootstrap";
import { BiChevronDown, BiChevronUp, BiPencil, BiPlus, BiPlusCircle, BiTransfer, BiTrash } from "react-icons/bi";
import { LoaderContext } from "../../../App";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import MaterialIssueService from "../../../core/service/MaterialIssueService";
import './MaterialIssues.scss'
import CompanyService from "../../../core/service/CompanyService";
import Utils from "../../../core/utilities/Utils";
import QuestionService from "../../../core/service/QuestionService";
import custom_tag from '../../../images/tags.png';

const MaterialIssues = ({ t }) => {

    const [activeTab, setActiveTab] = useState('ENVIRONMENTAL');
    const [issueCategory, setIssueCategory] = useState('GENERAL');
    const { setLoaderDisplay } = useContext(LoaderContext);
    const [parentTopics, setParentTopics] = useState([]);
    const [parentTopicVisiblity, setParentTopicVisiblity] = useState({});
    const [subTopics, setSubTopics] = useState({});
    const [editedTopic, setEditedTopic] = useState({ id: -1, parentId: -1, action: '' });
    const [validated, setValidated] = useState(false);
    const [childTopicVisiblity, setChildTopicVisiblity] = useState({});
    const [languages, setLanguages] = useState([]);
    const [topicFields, setTopicFields] = useState([
        {
            language: "en",
            name: "",
            description: ""
        }
    ]);
    const [additionalFields, setAdditionalFields] = useState({
        code: "",
        sortOrder: 0,
        issueCategory: ""
    });
    const [matIssueCategoryList, setMatIssueCategoryList] = useState([]);

    useEffect(() => {
        getParentTopics();
    }, [activeTab, issueCategory]);

    useEffect(() => {
        getMatIssuesCategory();
        getLanguages();
    }, []);

    const getMatIssuesCategory = () => {
        MaterialIssueService.getMatIssuesCategory()
            .then(res => {
                if (res.status !== 200) return;
                setMatIssueCategoryList(res.data.data ?? []);
            })
            .catch(err => {
            });
    };

    const getParentTopics = () => {
        setLoaderDisplay(true);
        MaterialIssueService.getParentTopics(activeTab, issueCategory).then(res => {
            setLoaderDisplay(false);
            if (res.status !== 200) return;
            const result = res.data.data;
            // result?.sort((a, b) => a.name.localeCompare(b.name));
            setParentTopics(result);
        }).catch(err => {
            setLoaderDisplay(false);
        });
    };

    const getChildTopics = (id, topicId) => {
        setLoaderDisplay(true);
        MaterialIssueService.getChildTopics(id).then(res => {
            setLoaderDisplay(false);
            if (res.status !== 200) return;
            const result = res.data.data;
            // result?.children?.sort((a, b) => a.name.localeCompare(b.name));
            if (topicId > 0) {
                // topicID > 0 means a sub sub topic was edited
                const s = JSON.parse(JSON.stringify(subTopics));
                const subTopicIndex = s[topicId].children?.findIndex(c => c.id === id);
                if (subTopicIndex >= 0) {
                    s[topicId].children[subTopicIndex].children = result.children;
                    setSubTopics(s);
                }
            } else {
                setSubTopics(prevState => ({
                    ...prevState,
                    [id]: result,
                }));
            }
        }).catch(err => {
            setLoaderDisplay(false);
        });
    };

    const upsertTopic = (e) => {
        e.preventDefault();

        if (topicFields.some(field => !field.language || !field.name.trim() || !field.description.trim())) {
            setValidated(true);
            return;
        }

        const englishEntry = topicFields.find(field => field.language === "en");

        const payload = {
            id: editedTopic.id > 0 ? editedTopic.id : null,
            code: additionalFields.code || "",
            sortOrder: additionalFields.sortOrder || 0,
            issueCategory: issueCategory || "GENERAL",
            name: englishEntry?.name || "",
            description: englishEntry?.description || "",
            parentId: editedTopic.parentId > 0 ? editedTopic.parentId : null,
            category: activeTab,
            localizedContents: topicFields.map(field => ([
                {
                    fieldName: "name",
                    languageCode: field.language,
                    translatedValue: field.name
                },
                {
                    fieldName: "description",
                    languageCode: field.language,
                    translatedValue: field.description
                }
            ])).flat()
        };

        setLoaderDisplay(true);
        MaterialIssueService.addTopic(payload).then(res => {
            setLoaderDisplay(false);
            if (res.status !== 200) return;

            toast.success('Data added successfully', {
                position: toast.POSITION.TOP_RIGHT,
            });

            if (editedTopic.action === 'ADD PARENT' || editedTopic.action === 'EDIT PARENT') {
                getParentTopics();
            } else {
                getChildTopics(editedTopic.parentId, editedTopic.topicId);
            }
            setValidated(false);
            setEditedTopic({ id: -1, parentId: -1, action: '' });
            setTopicFields([{ language: "en", name: "", description: "" }]);
            setAdditionalFields({ code: "", sortOrder: 0, issueCategory: "" });
        }).catch(err => {
            setLoaderDisplay(false);
        });
    };

    const deleteTopic = () => {
        setLoaderDisplay(true);
        MaterialIssueService.deleteTopic(editedTopic.id).then(res => {
            setLoaderDisplay(false);
            if (res.status !== 200) return;

            if (res.data.status === 'Failed') {
                toast.error(res.data.message, {
                    position: toast.POSITION.TOP_RIGHT,
                });
                return;
            }
            toast.success('Data deleted successfully', {
                position: toast.POSITION.TOP_RIGHT,
            });

            if (editedTopic.action === 'DELETE PARENT') {
                getParentTopics();
            } else if (editedTopic.action === 'DELETE CHILD') {
                getChildTopics(editedTopic.parentId, editedTopic.topicId);
            } else {
                getChildTopics(editedTopic.parentId, editedTopic.topicId);
            }
            setEditedTopic({ id: -1, parentId: -1, action: '' });
            setTopicFields([{ language: "en", name: "", description: "" }]);
            setAdditionalFields({ code: "", sortOrder: 0, issueCategory: "" });
        }).catch(err => {
            setLoaderDisplay(false);
        });
    };

    const getLanguages = () => {
        CompanyService.getLanguageList()
            .then((response) => {
                setLanguages([...response.data.data]);
            })
            .catch((error) => { });
    };

    const handleTranslate = (targetLang, index) => {
        const inputText = [];
        inputText.push(topicFields[0]?.name);
        inputText.push(topicFields[0]?.description);
        const sourceLang = topicFields[0]?.language;

        if (topicFields[0]?.name && topicFields[0]?.description && sourceLang && targetLang) {
            QuestionService.translateText(inputText, sourceLang, targetLang).then(res => {
                if (res.status !== 200) return;

                const result = res.data.translations;
                const updatedTopicFields = [...topicFields];
                updatedTopicFields[index] = {
                    ...updatedTopicFields[index],
                    name: result[0]?.text,
                    description: result[1]?.text,
                };
                setTopicFields(updatedTopicFields);
            }).catch(err => { });
        } else {
            if (!sourceLang || !targetLang) {
                toast.warning('Please provide source and target languages', {
                    position: toast.POSITION.TOP_RIGHT,
                });
            } else if (!topicFields[0]?.name || !topicFields[0]?.description) {
                toast.warning('Please provide input topic and description', {
                    position: toast.POSITION.TOP_RIGHT,
                });
            }
        }
    }

    const getAvailableLanguages = (index) => {
        const selectedLanguages = topicFields.map(field => field.language);
        return languages.filter(lang => !selectedLanguages.includes(lang.code) || lang.code === topicFields[index].language);
    };

    const handleAddRow = () => {
        if (topicFields.length < languages.length) {
            setTopicFields([...topicFields, { language: "", name: "", description: "" }]);
        }
    };

    const handleRemoveRow = (index) => {
        if (topicFields.length > 1) {
            setTopicFields(topicFields.filter((_, i) => i !== index));
        }
    };

    const handleInputChange = (index, field, value) => {
        const newFields = [...topicFields];
        newFields[index][field] = value;
        setTopicFields(newFields);
    };

    const updateTopicFieldsForModal = (content) => {
        const updatedTopicFields = [];

        if (content.localizedContents.length > 0) {
            content?.localizedContents.forEach((item) => {
                const { languageCode, fieldName, translatedValue } = item;

                let topicField = updatedTopicFields.find(field => field.language === languageCode);

                if (!topicField) {
                    topicField = { language: languageCode, name: "", description: "" };
                    updatedTopicFields.push(topicField);
                }

                if (fieldName === "name") {
                    topicField.name = translatedValue;
                } else if (fieldName === "description") {
                    topicField.description = translatedValue;
                }
            });

            updatedTopicFields.sort((a, b) => (a.language === "en" ? -1 : b.language === "en" ? 1 : 0));

            setTopicFields(updatedTopicFields);
            setAdditionalFields({ code: content.code, sortOrder: content.sortOrder, issueCategory: content.issueCategory });
        } else {
            setTopicFields([{ language: "en", name: content.name, description: content.description }]);
            setAdditionalFields({ code: "", sortOrder: 0, issueCategory: "" });
        }
    }


    const renderMaterialIssues = () => {
        return (
            <div className="MaterialIssues_SetCard">
                <Table>
                    <tbody>
                        {renderMaterialTopics()}
                    </tbody>
                </Table>
                <div className="MaterialIssues_SetCard_Footer">
                    <div>
                        <Button variant="secondary" size="sm"
                            onClick={() => {
                                setEditedTopic({ id: 0, parentId: 0, action: 'ADD PARENT' });
                                setTopicFields([{ language: "en", name: "", description: "" }]);
                                setAdditionalFields({ code: "", sortOrder: 0, issueCategory: "" });
                            }}
                        >
                            <BiPlus className="me-1" /> {t("Topic.label")}</Button>
                    </div>
                </div>
            </div>
        )
    }

    const renderMaterialTopics = () => {
        return (
            <>
                {
                    parentTopics?.map((parent, pIndex) => {
                        return (
                            <>
                                <tr id={`parentTopicRow-${pIndex}`} key={`parentTopicRow-${pIndex}`} className="parentTopic-row ">
                                    <td>
                                        <div className="icon_svg cursor" onClick={() => toggleParentVisibility(parent.id)}>
                                            {!parentTopicVisiblity[parent.id] && <BiChevronDown />}
                                            {parentTopicVisiblity[parent.id] === true && <BiChevronUp />}
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            <h6 className="mb-0">{parent.name}</h6>
                                            {parent.companyRef && (
                                               <OverlayTrigger
                                               placement="top"
                                               overlay={Utils.renderTooltip(
                                                   `${t('CreatedBy.label')} - ${parent.companyName}`
                                               )}>
                                               <span className='custom_tag_img'><img src={custom_tag} alt="custom_tag" /></span>
                                           </OverlayTrigger>
                                            )}
                                        </div>
                                    </td>
                                    <td></td>
                                    <td className="text-end">
                                        {!parent.companyRef && (
                                            <>
                                                <span className="p-1 cursor"
                                                    onClick={() => {
                                                        setEditedTopic({ id: parent.id, parentId: -1, action: 'EDIT PARENT' });
                                                        updateTopicFieldsForModal(parent);
                                                    }}
                                                >
                                                    <BiPencil />
                                                </span>
                                                <span className="p-1 cursor"
                                                    onClick={() => setEditedTopic({ id: parent.id, parentId: -1, action: 'DELETE PARENT' })}><BiTrash /></span>
                                            </>
                                        )}
                                    </td>
                                </tr>
                                {parentTopicVisiblity[parent.id] === true && renderChildTopics(parent, pIndex, subTopics[parent.id])}
                            </>
                        );
                    })
                }
            </>
        );
    };

    const toggleParentVisibility = (parentId) => {
        const p = { ...parentTopicVisiblity };

        if (p[parentId] === true) {
            p[parentId] = false;
        } else {
            p[parentId] = true;
            getChildTopics(parentId);
        }
        setParentTopicVisiblity(p);
    };

    const toggleChildVisibility = (parentId) => {
        const p = { ...childTopicVisiblity };

        if (p[parentId] === true) {
            p[parentId] = false;
        } else {
            p[parentId] = true;
            // getChildTopics(parentId);
        }
        setChildTopicVisiblity(p);
    };

    const renderChildTopics = (parent, pIndex, childTopics) => {
        return (
            <>
                {
                    childTopics?.children?.map((child, cIndex) => {
                        return (
                            <>
                                <tr id={`subTopicRow-${pIndex}-${cIndex}`} key={`subTopicRow-${pIndex}-${cIndex}`}
                                    className="subTopic-row">
                                    <td>
                                        <div className="subTopic_in">
                                            <div className="icon_svg cursor" onClick={() => toggleChildVisibility(child.id)}>
                                                {!childTopicVisiblity[child.id] && <BiChevronDown />}
                                                {childTopicVisiblity[child.id] === true && <BiChevronUp />}
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                                <h6 className="mb-0">{child.name}</h6>
                                                {child.companyRef && (
                                                    // <span className="badge bg-primary text-white">{t("CompanyCreated.label")}</span>
                                                    <OverlayTrigger
                                               placement="top"
                                               overlay={Utils.renderTooltip(
                                                   `${t('CreatedBy.label')} - ${child.companyName}`
                                               )}>
                                               <span className='custom_tag_img'><img src={custom_tag} alt="custom_tag" /></span>
                                           </OverlayTrigger>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td></td>
                                    <td className="text-end">
                                        {!child.companyRef && (
                                            <>
                                                <span className="p-1 cursor"
                                                    onClick={() => {
                                                        setEditedTopic({ id: child.id, parentId: parent.id, action: 'EDIT CHILD' });
                                                        updateTopicFieldsForModal(child);
                                                    }}
                                                >
                                                    <BiPencil />
                                                </span>
                                                <span className="p-1 cursor"
                                                    onClick={() => setEditedTopic({ id: child.id, parentId: parent.id, action: 'DELETE CHILD' })}><BiTrash /></span>
                                            </>
                                        )}
                                    </td>
                                </tr>
                                {childTopicVisiblity[child.id] === true && renderSubSubTopics(child, parent.id, pIndex)}
                            </>
                        );
                    })
                }

                {
                    <tr className="subTopic-row">
                        <td colSpan={4}>
                            <span className="cursor text-primary fw-600"
                                onClick={() => {
                                    setEditedTopic({ id: 0, parentId: parent.id, action: 'ADD CHILD' });
                                    setTopicFields([{ language: "en", name: "", description: "" }]);
                                    setAdditionalFields({ code: "", sortOrder: 0, issueCategory: "" });
                                }}
                            >
                                <BiPlus /> {t("SubTopic.label")}</span>
                        </td>
                    </tr>
                }
            </>
        );
    };

    const renderSubSubTopics = (subTopic, topicId, pIndex) => {
        return (
            <>
                {
                    subTopic?.children?.map((child, cIndex) => {
                        return (
                            <>
                                <tr id={`subsubTopicRow-${pIndex}-${cIndex}`} key={`subsubTopicRow-${pIndex}-${cIndex}`}
                                    className="subsubTopic-row">
                                    <td>
                                        <div className="subsubTopic_in">
                                            <div className="d-flex align-items-center gap-2">
                                                <h6 className="mb-0">{child.name}</h6>
                                                {child.companyRef && (
                                                    //  <span className="badge bg-primary text-white">{t("CompanyCreated.label")}</span>
                                                    <OverlayTrigger
                                               placement="top"
                                               overlay={Utils.renderTooltip(
                                                   `${t('CreatedBy.label')} - ${child.companyName}`
                                               )}>
                                               <span className='custom_tag_img'><img src={custom_tag} alt="custom_tag" /></span>
                                           </OverlayTrigger>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td></td>
                                    <td className="text-end">
                                        {!child.companyRef && (
                                            <>
                                                <span className="p-1 cursor"
                                                    onClick={() => {
                                                        setEditedTopic({ id: child.id, parentId: subTopic.id, topicId: topicId, action: 'EDIT SUB CHILD' });
                                                        updateTopicFieldsForModal(child);
                                                    }}
                                                >
                                                    <BiPencil />
                                                </span>
                                                <span className="p-1 cursor"
                                                    onClick={() => setEditedTopic({ id: child.id, parentId: subTopic.id, topicId: topicId, action: 'DELETE SUB CHILD' })}><BiTrash /></span>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            </>
                        );
                    })
                }

                {
                    <tr className="subTopic-row">
                        <td colSpan={4}>
                            <span className="cursor text-primary fw-600"
                                onClick={() => {
                                    setEditedTopic({ id: 0, parentId: subTopic.id, topicId: topicId, action: 'ADD SUB CHILD' });
                                    setTopicFields([{ language: "en", name: "", description: "" }]);
                                    setAdditionalFields({ code: "", sortOrder: 0, issueCategory: "" });
                                }}
                            >
                                <BiPlus /> {t("SubSubTopic.label")}</span>
                        </td>
                    </tr>
                }
            </>
        );
    };

    const renderTopicAndSubTopicModal = () => {
        if (editedTopic.id === -1 || editedTopic.action === 'DELETE PARENT' || editedTopic.action === 'DELETE CHILD' || editedTopic.action === 'DELETE SUB CHILD') return <></>;
        return (
            <Modal show={editedTopic.id >= 0}
                size='xl'
                onHide={() => {
                    setEditedTopic({ id: -1, parentId: -1, action: '' });
                    setTopicFields([{ language: "en", name: "", description: "" }]);
                    setAdditionalFields({ code: "", sortOrder: 0, issueCategory: "" });
                    setValidated(false);
                }}
                aria-labelledby="contained-modal-title-vcenter"
                centered>
                <Modal.Header closeButton>
                    < Modal.Title as="h5">
                        {
                            editedTopic.action === 'ADD PARENT' ? t("NewTopic.label")
                                : editedTopic.action === 'EDIT PARENT' ? t("UpdateTopic.label")
                                    : editedTopic.action === 'ADD CHILD' ? t("AddSubTopic.label")
                                        : editedTopic.action === 'EDIT CHILD' ? t("UpdateSubTopic.label")
                                            : editedTopic.action === 'ADD SUB CHILD' ? t("AddSubSubTopic.label")
                                                : t("UpdateSubSubTopic.label")
                        }
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form noValidate validated={validated}>
                        <div className="row m-0">
                            <div className="col-lg-3 p-2">
                                <label>Category</label>
                                <select className="form-select"
                                    value={issueCategory} disabled={true}>
                                    {
                                        matIssueCategoryList?.map((e, index) => {
                                            return <option value={e.name} key={e.id}>{e.name}</option>;
                                        })
                                    }
                                </select>
                            </div>
                            <div className="col-lg-3 p-2">
                                <label>Code</label>
                                <input type="text" className="form-control" value={additionalFields.code}
                                    onChange={(e) => setAdditionalFields({ ...additionalFields, code: e.target.value })}
                                    required />
                            </div>
                            <div className="col-lg-3 p-2">
                                <label>Sort Order</label>
                                <input type="number" className="form-control" value={additionalFields.sortOrder}
                                    onChange={(e) => setAdditionalFields({ ...additionalFields, sortOrder: e.target.value })}
                                    required />
                            </div>
                        </div>
                        <div className="row m-0">
                            <div className="col-lg-2 p-2">
                                <label className="mb-1">
                                    {t("Languages.label")} <span className="text-danger">*</span>
                                </label>
                            </div>
                            <div className="col-lg-4 p-2">
                                <label className="mb-1">
                                    {editedTopic.action === 'ADD PARENT' || editedTopic.action === 'EDIT PARENT'
                                        ? `${t("Topic.label")}`
                                        : `${t("SubTopic.label")}`} <span className="text-danger">*</span>
                                </label>
                            </div>
                            <div className="col-lg-4 p-2">
                                <label className="mb-1">{t("Description.label")} <span className="text-danger">*</span></label>
                            </div>
                            <div className='col-lg-2 px-2 py-1'>
                                <label className="mb-1"></label>
                            </div>
                        </div>
                        {topicFields.map((field, index) => (
                            <div className="row m-0" key={index}>
                                <div className="col-lg-2 p-2">
                                    <select
                                        className="form-select"
                                        value={field.language}
                                        disabled={field.language === 'en'}
                                        onChange={(e) => handleInputChange(index, "language", e.target.value)}
                                        required
                                    >
                                        <option value="">Select</option>
                                        {getAvailableLanguages(index).map((lang) => (
                                            <option value={lang.code} key={lang.id}>{lang.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="col-lg-4 p-2">
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={field.name}
                                        onChange={(e) => handleInputChange(index, "name", e.target.value)}
                                        required
                                        maxLength={250}
                                    />
                                </div>
                                <div className="col-lg-4 p-2">
                                    <textarea
                                        className="form-control"
                                        value={field.description}
                                        onChange={(e) => handleInputChange(index, "description", e.target.value)}
                                        required
                                        maxLength={2000}
                                    />
                                </div>
                                <div className="col-lg-2 px-2 py-1">
                                    <ul className="stakeCat_add">
                                        {index > 0 && (
                                            <OverlayTrigger placement="top" overlay={Utils.renderTooltip("Click to Translate")}>
                                                <li>
                                                    <span className='cursor' onClick={() => handleTranslate(field.language, index)}>
                                                        <BiTransfer />
                                                    </span>
                                                </li>
                                            </OverlayTrigger>
                                        )}
                                        {topicFields.length > 1 && (
                                            <OverlayTrigger placement="top" overlay={Utils.renderTooltip("Delete")}>
                                                <li>
                                                    <span className='cursor' onClick={() => handleRemoveRow(index)}>
                                                        <BiTrash />
                                                    </span>
                                                </li>
                                            </OverlayTrigger>
                                        )}
                                        {topicFields.length - 1 === index && topicFields.length < languages.length && (
                                            <OverlayTrigger placement="top" overlay={Utils.renderTooltip("Add new")}>
                                                <li>
                                                    <Button variant="GroupStake" className="btn-outline-primary border-0" onClick={handleAddRow}>
                                                        <BiPlusCircle />
                                                    </Button>
                                                </li>
                                            </OverlayTrigger>
                                        )}
                                    </ul>
                                </div>
                            </div>
                        ))}
                        <div className="col-lg-12 p-2 text-center">
                            <button className="btn btn-secondary me-2 px-3"
                                onClick={() => {
                                    setEditedTopic({ id: -1, parentId: -1, action: '' });
                                    setTopicFields([{ language: "en", name: "", description: "" }]);
                                    setAdditionalFields({ code: "", sortOrder: 0, issueCategory: "" });
                                    setValidated(false);
                                }}>
                                {t("Discard.label")}
                            </button>
                            <button className="btn btn-primary px-4" onClick={upsertTopic}>{t("Save.label")}</button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
        );
    };

    const renderDeleteTopic = () => {
        if (editedTopic.action !== 'DELETE PARENT' && editedTopic.action !== 'DELETE CHILD' && editedTopic.action !== 'DELETE SUB CHILD') return <></>;
        return (
            <ConfirmationModal
                modalShow={editedTopic.id >= 0 ? true : false}
                messageText={
                    editedTopic.action === 'DELETE PARENT' ? t("DeleteTopic.label") : 'DELETE CHILD' ? t("DeleteSubTopic.label") : t("DeleteSubSubTopic.label")
                }
                callbackModal={(val) => {
                    setEditedTopic({ id: -1, parentId: -1, action: '' });
                    if (val) {
                        deleteTopic();
                    }
                }}
                confirmBtn={t("Confirm.label")}
                CancelBtn={t("Cancel.label")}
            />
        );
    };

    const renderTabs = () => {
        return (
            <Tabs
                id="controlled-tab-example"
                activeKey={activeTab}
                onSelect={(k) => onTabchange(k)}
                className="MaterialIssues_tabs">
                <Tab eventKey="ENVIRONMENTAL" title="Enivronmental">
                    {renderMaterialIssues()}
                </Tab>
                <Tab eventKey="SOCIAL" title="Social">
                    {renderMaterialIssues()}
                </Tab>
                <Tab eventKey="GOVERNANCE" title="Governance" >
                    {renderMaterialIssues()}
                </Tab>
            </Tabs>
        )
    };

    const onTabchange = (k) => {
        setParentTopicVisiblity({});
        setActiveTab(k);
    };

    return (
        <div className="MaterialIssues">
            <div className="List_header">
                <h5 className="MH">{t("MaterialIssues.label")}</h5>
                <Form.Select size="sm" className="w-auto ms-2"
                    onChange={(e) => setIssueCategory(e.target.value)}
                    selected={issueCategory}>
                    {
                        matIssueCategoryList?.map((e, index) => {
                            return <option value={e.name} key={e.id}>{e.name}</option>;
                        })
                    }
                </Form.Select>
            </div>
            <div className="row m-0">
                <div className="MaterialIssues_card col-lg-12 p-2">
                    <div className="card">
                        <div className="MaterialIssues_card_body">
                            {renderTabs()}
                        </div>
                        {renderTopicAndSubTopicModal()}
                        {renderDeleteTopic()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default withTranslation()(MaterialIssues);
